import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import {
  Phone,
  Mail,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Shield,
  Volume2,
  Grip,
  SprayCan,
  LayoutGrid,
  Palette,
  Dumbbell,
  Swords,
  Zap,
  Target,
  Check,
  ArrowRight,
  Menu,
  X,
  Instagram,
  Facebook,
  Globe,
  Truck,
  Factory,
  FileText,
  Headphones,
  Play,
  Calculator,
  Layers,
  Square,
  Link2 } from
"lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"./components/ui/select";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Assets from client
const ASSETS = {
  hero: "https://customer-assets.emergentagent.com/job_39e03785-56f8-418a-9f60-c62e6932ad0a/artifacts/if2gouaa_Degoma_fabricacion_suelos_caucho_joven.jpeg",
  warehouse: "https://customer-assets.emergentagent.com/job_39e03785-56f8-418a-9f60-c62e6932ad0a/artifacts/ovm47mlt_degoma_fabricante_suelos_caucho.jpeg",
  texture: "https://customer-assets.emergentagent.com/job_39e03785-56f8-418a-9f60-c62e6932ad0a/artifacts/xkoegxn4_suelos-caucho_grano_fino.jpeg",
  video2: "https://customer-assets.emergentagent.com/job_39e03785-56f8-418a-9f60-c62e6932ad0a/artifacts/n9zi9yl9_video_suelo_degoma_gym.mp4",
  // Gallery images
  gymBenches: "https://customer-assets.emergentagent.com/job_losetas-100x100/artifacts/k10a90u2_caucho_reciclado_gimnasios3.jpg",
  gymFunctional: "https://customer-assets.emergentagent.com/job_losetas-100x100/artifacts/zzdol0gw_ChatGPT%20Image%208%20ago%202025%2C%2014_33_13.png",
  shipping: "https://customer-assets.emergentagent.com/job_losetas-100x100/artifacts/vsnjg8gf_suelos_caucho_fabricantes_palets_exportacion.jpg",
  athleteTop: "https://customer-assets.emergentagent.com/job_losetas-100x100/artifacts/bdfh1q5b_Suelos_gimnasios_fitness.png",
  gymNeon: "https://customer-assets.emergentagent.com/job_losetas-100x100/artifacts/7o1y7qgf_Suelos_gimnasios_fitness2.jpg",
  crossfitGuy: "https://customer-assets.emergentagent.com/job_losetas-100x100/artifacts/9bjz9vdp_degoma-gym.jpg",
  factoryTruck: "https://customer-assets.emergentagent.com/job_losetas-100x100/artifacts/golp0usa_degoma_fabricantes_suelos_caucho_SBR_parques_infantiles.jpg"
};

// Thickness options
const THICKNESS_OPTIONS = [
{
  value: "1cm",
  label: "1 cm",
  use: "Cardio / Mantenimiento",
  description: "Orientado a zonas de cardio, máquinas estáticas y tránsito ligero."
},
{
  value: "1.5cm",
  label: "1,5 cm",
  use: "Fitness General",
  description: "Diseñado para salas de fitness, clases colectivas y uso moderado."
},
{
  value: "2cm",
  label: "2 cm",
  use: "Funcional",
  description: "Pensado para entrenamiento funcional, CrossFit básico y peso medio.",
  popular: true
},
{
  value: "3cm",
  label: "3 cm",
  use: "Peso Libre",
  description: "Adecuado para zonas de peso libre, mancuernas y discos medios."
},
{
  value: "4cm",
  label: "4 cm",
  use: "Alta Carga",
  description: "Máxima protección para halterofilia, caída de peso y alta intensidad.",
  badge: "Alta protección"
}];


// Gym types
const GYM_TYPES = [
"Fitness / Musculación",
"CrossFit / Box",
"Boxeo / Artes Marciales",
"Entrenamiento Personal",
"Halterofilia",
"Spinning / Cardio",
"Centro Polideportivo",
"Otro"];


// FAQ data
const FAQ_DATA = [
{
  question: "¿Qué espesor necesito para mi gimnasio?",
  answer: "Depende del tipo de actividad y equipamiento. Para cardio y máquinas ligeras, 1-1,5 cm suele ser suficiente. Para entrenamiento funcional y peso moderado, recomendamos 2-3 cm. Para zonas de halterofilia con caída de peso, el espesor de 4 cm ofrece mayor protección. Te ayudamos a elegir en función de tu uso específico."
},
{
  question: "¿Cómo se instalan las losetas?",
  answer: "Las losetas de 100x100 cm están diseñadas para instalación sencilla. Puedes colocarlas en forma flotante (sin adhesivos), con cinta de doble cara o pegadas. La instalación flotante permite reubicar fácilmente si cambias la distribución del espacio."
},
{
  question: "¿Qué ventaja tiene el acabado de grano ultrafino?",
  answer: "El acabado de grano ultrafino está orientado a facilitar la limpieza diaria y proporcionar una superficie más homogénea visualmente. La textura permite un agarre adecuado para el entrenamiento."
},
{
  question: "¿Hacéis envíos a toda España?",
  answer: "Sí, enviamos a toda España: Península, Baleares y Canarias. El plazo de entrega es rápido gracias a que trabajamos con material en stock. Te confirmamos el plazo exacto según tu ubicación al solicitar presupuesto."
},
{
  question: "¿Puedo combinar diferentes espesores?",
  answer: "Sí, es habitual combinar espesores según las zonas del gimnasio. Por ejemplo, 4 cm en la zona de peso libre y 1,5 cm en la zona de cardio. Te asesoramos para optimizar tu inversión según la distribución de tu espacio."
},
{
  question: "¿Las losetas son resistentes a equipamiento pesado?",
  answer: "Las losetas están diseñadas para uso intensivo en gimnasios profesionales. La resistencia depende del espesor elegido: a mayor espesor, mayor capacidad de absorción de impacto y protección del suelo base."
},
{
  question: "¿Necesito preparar el suelo antes de instalar?",
  answer: "El suelo base debe estar limpio, seco y razonablemente nivelado. No es necesario hacer obra ni aplicar productos especiales en la mayoría de los casos. Si tienes dudas sobre tu superficie, consúltanos."
},
{
  question: "¿Emitís factura?",
  answer: "Sí, emitimos factura completa con todos los datos fiscales para empresas y autónomos. Es importante para la deducción del IVA en tu actividad."
},
{
  question: "¿Cuántas losetas necesito?",
  answer: "Cada loseta cubre 1 m² (100x100 cm). El número de losetas equivale a los metros cuadrados de tu superficie, más un pequeño margen para cortes si tu espacio no es rectangular. Te lo calculamos exacto en el presupuesto."
},
{
  question: "¿Puedo ver el material antes de comprar?",
  answer: "Puedes solicitar información detallada y fotos adicionales del producto. También puedes ver nuestros vídeos donde se aprecia la textura y acabado real del material. Contáctanos y resolvemos cualquier duda."
}];


// Benefits data
const BENEFITS = [
{
  icon: Shield,
  title: "Protege suelo y equipamiento",
  description: "Según uso y espesor elegido"
},
{
  icon: Volume2,
  title: "Reduce ruido y vibración",
  description: "Ayuda a minimizar el impacto"
},
{
  icon: Grip,
  title: "Superficie con agarre",
  description: "Orientado a estabilidad"
},
{
  icon: SprayCan,
  title: "Limpieza sencilla",
  description: "Acabado grano ultrafino"
},
{
  icon: LayoutGrid,
  title: "Formato modular",
  description: "Losetas 100x100 cm"
},
{
  icon: Palette,
  title: "Acabado profesional",
  description: "Negro mate uniforme"
}];


// Use cases
const USE_CASES = [
{
  icon: Dumbbell,
  title: "Fitness & Salas Privadas",
  pain: "Proteger el suelo de máquinas y pesas",
  benefit: "Absorción de impacto y fácil mantenimiento",
  thickness: "1,5 - 2 cm"
},
{
  icon: Zap,
  title: "CrossFit / Box",
  pain: "Resistir caídas de peso y uso intensivo",
  benefit: "Alta durabilidad para entrenamiento funcional",
  thickness: "3 - 4 cm"
},
{
  icon: Swords,
  title: "Boxeo & Artes Marciales",
  pain: "Amortiguación para saltos y desplazamientos",
  benefit: "Superficie estable con agarre adecuado",
  thickness: "2 - 3 cm"
},
{
  icon: Target,
  title: "Zona de Fuerza",
  pain: "Soportar peso muerto y caída de discos",
  benefit: "Máxima protección del suelo base",
  thickness: "3 - 4 cm"
}];


function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedThickness, setSelectedThickness] = useState("2cm");
  const [calculatorValue, setCalculatorValue] = useState("");
  const [openFaq, setOpenFaq] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "",
    ciudad: "",
    superficie: "",
    tipo_gimnasio: "",
    espesores: [],
    mensaje: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  const handleThicknessToggle = (value) => {
    setFormData((prev) => ({
      ...prev,
      espesores: prev.espesores.includes(value) ?
      prev.espesores.filter((e) => e !== value) :
      [...prev.espesores, value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre || !formData.email || !formData.telefono || !formData.ciudad || !formData.superficie || !formData.tipo_gimnasio) {
      toast.error("Por favor, completa todos los campos obligatorios");
      return;
    }

    if (formData.espesores.length === 0) {
      toast.error("Selecciona al menos un espesor de interés");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/quote`, {
        ...formData,
        superficie: parseFloat(formData.superficie)
      });

      if (response.data) {
        toast.success("¡Solicitud enviada! Te contactaremos pronto.");
        setFormData({
          nombre: "",
          email: "",
          telefono: "",
          ciudad: "",
          superficie: "",
          tipo_gimnasio: "",
          espesores: [],
          mensaje: ""
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Error al enviar. Por favor, inténtalo de nuevo o contacta por WhatsApp.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const whatsappNumber = "34618265667";

  // Función para abrir WhatsApp de forma compatible
  const openWhatsApp = () => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    if (isMobile) {
      // En móvil, usar el protocolo directo de WhatsApp
      window.location.href = `whatsapp://send?phone=${whatsappNumber}`;
    } else {
      // En escritorio, abrir WhatsApp Web en nueva pestaña
      window.open(`https://web.whatsapp.com/send?phone=${whatsappNumber}`, '_blank');
    }
  };

  // Scroll to top visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[var(--degoma-black)]">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 glass" data-testid="header">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a href="#" className="logo-text" data-testid="logo" onClick={() => scrollToSection('hero')}>
              DEGOMA<sup style={{ fontSize: '12px' }}>®</sup>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              <button onClick={() => scrollToSection('espesores')} className="nav-link">Espesores</button>
              <button onClick={() => scrollToSection('ventajas')} className="nav-link">Ventajas</button>
              <button onClick={() => scrollToSection('instalacion')} className="nav-link">Instalación</button>
              <button onClick={() => scrollToSection('usos')} className="nav-link">Casos de uso</button>
              <button onClick={() => scrollToSection('galeria')} className="nav-link">Galería</button>
              <button onClick={() => scrollToSection('faq')} className="nav-link">FAQ</button>
              <button onClick={() => scrollToSection('contacto')} className="nav-link">Contacto</button>
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={openWhatsApp}
                className="flex items-center gap-2 text-[var(--degoma-text)] hover:text-white transition-colors text-sm cursor-pointer"
                data-testid="header-whatsapp">

                <MessageCircle size={18} className="text-[#25D366]" />
                <span className="hidden xl:inline">WhatsApp</span>
              </button>
              <a
                href="tel:618265712"
                className="flex items-center gap-2 text-[var(--degoma-text)] hover:text-white transition-colors text-sm"
                data-testid="header-phone">

                <Phone size={18} className="text-[var(--degoma-red)]" />
                <span className="hidden xl:inline">618 265 712</span>
              </a>
              <Button
                onClick={() => scrollToSection('contacto')}
                className="btn-primary px-5 py-2 text-sm"
                data-testid="header-cta">

                Pedir presupuesto
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn">

              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen &&
        <div className="lg:hidden absolute top-full left-0 right-0 bg-[var(--degoma-dark)] border-t border-[var(--degoma-border)] animate-slide-up max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
            <nav className="flex flex-col p-4 gap-1">
              <button 
                onClick={() => scrollToSection('espesores')} 
                className="text-left p-4 rounded-lg hover:bg-[var(--degoma-surface)] transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[var(--degoma-surface)] group-hover:bg-[var(--degoma-red)] flex items-center justify-center transition-colors">
                    <Layers size={20} className="text-[var(--degoma-red)] group-hover:text-white" />
                  </div>
                  <div>
                    <span className="block text-white font-bold uppercase tracking-wide">Espesores</span>
                    <span className="block text-[var(--degoma-text)] text-xs mt-0.5">Encuentra el grosor exacto para tu aplicación</span>
                  </div>
                </div>
              </button>
              
              <button 
                onClick={() => scrollToSection('ventajas')} 
                className="text-left p-4 rounded-lg hover:bg-[var(--degoma-surface)] transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[var(--degoma-surface)] group-hover:bg-[var(--degoma-red)] flex items-center justify-center transition-colors">
                    <Shield size={20} className="text-[var(--degoma-red)] group-hover:text-white" />
                  </div>
                  <div>
                    <span className="block text-white font-bold uppercase tracking-wide">Ventajas</span>
                    <span className="block text-[var(--degoma-text)] text-xs mt-0.5">¡Descubre qué nos hace diferentes!</span>
                  </div>
                </div>
              </button>
              
              <button 
                onClick={() => scrollToSection('instalacion')} 
                className="text-left p-4 rounded-lg hover:bg-[var(--degoma-surface)] transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[var(--degoma-surface)] group-hover:bg-[var(--degoma-red)] flex items-center justify-center transition-colors">
                    <LayoutGrid size={20} className="text-[var(--degoma-red)] group-hover:text-white" />
                  </div>
                  <div>
                    <span className="block text-white font-bold uppercase tracking-wide">Instalación</span>
                    <span className="block text-[var(--degoma-text)] text-xs mt-0.5">Guía paso a paso para una colocación perfecta</span>
                  </div>
                </div>
              </button>
              
              <button 
                onClick={() => scrollToSection('usos')} 
                className="text-left p-4 rounded-lg hover:bg-[var(--degoma-surface)] transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[var(--degoma-surface)] group-hover:bg-[var(--degoma-red)] flex items-center justify-center transition-colors">
                    <Target size={20} className="text-[var(--degoma-red)] group-hover:text-white" />
                  </div>
                  <div>
                    <span className="block text-white font-bold uppercase tracking-wide">Casos de uso</span>
                    <span className="block text-[var(--degoma-text)] text-xs mt-0.5">Proyectos reales</span>
                  </div>
                </div>
              </button>
              
              <button 
                onClick={() => scrollToSection('galeria')} 
                className="text-left p-4 rounded-lg hover:bg-[var(--degoma-surface)] transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[var(--degoma-surface)] group-hover:bg-[var(--degoma-red)] flex items-center justify-center transition-colors">
                    <Play size={20} className="text-[var(--degoma-red)] group-hover:text-white" />
                  </div>
                  <div>
                    <span className="block text-white font-bold uppercase tracking-wide">Galería</span>
                    <span className="block text-[var(--degoma-text)] text-xs mt-0.5">Inspírate en gimnasios ya funcionando</span>
                  </div>
                </div>
              </button>
              
              <button 
                onClick={() => scrollToSection('faq')} 
                className="text-left p-4 rounded-lg hover:bg-[var(--degoma-surface)] transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[var(--degoma-surface)] group-hover:bg-[var(--degoma-red)] flex items-center justify-center transition-colors">
                    <MessageCircle size={20} className="text-[var(--degoma-red)] group-hover:text-white" />
                  </div>
                  <div>
                    <span className="block text-white font-bold uppercase tracking-wide">FAQ</span>
                    <span className="block text-[var(--degoma-text)] text-xs mt-0.5">Resuelve tus dudas antes de comprar</span>
                  </div>
                </div>
              </button>
              
              <button 
                onClick={() => scrollToSection('contacto')} 
                className="text-left p-4 rounded-lg bg-[var(--degoma-red)]/10 border border-[var(--degoma-red)]/30 hover:bg-[var(--degoma-red)]/20 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[var(--degoma-red)] flex items-center justify-center">
                    <Mail size={20} className="text-white" />
                  </div>
                  <div>
                    <span className="block text-white font-bold uppercase tracking-wide">Contacto</span>
                    <span className="block text-[var(--degoma-text)] text-xs mt-0.5">Habla con un experto y obtén presupuesto gratis</span>
                  </div>
                </div>
              </button>
              
              <div className="flex gap-3 mt-4 pt-4 border-t border-[var(--degoma-border)]">
                <Button onClick={openWhatsApp} className="btn-whatsapp flex-1 py-4">
                  <MessageCircle size={18} /> WhatsApp
                </Button>
                <a href="tel:618265712" className="flex-1">
                  <Button variant="outline" className="w-full py-4 border-[var(--degoma-border)] text-white">
                    <Phone size={18} /> Llamar
                  </Button>
                </a>
              </div>
            </nav>
          </div>
        }
      </header>

      {/* Hero Section */}
      <section id="hero" className="hero-section" data-testid="hero-section">
        <div className="hero-bg">
          <video 
            src={ASSETS.video2}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
            onLoadedData={(e) => e.target.play()}
          />
          <div className="hero-overlay" />
        </div>
        
        <div className="hero-content">
          <div className="animate-fade-in-up">
            <p className="text-[var(--degoma-red)] font-semibold text-sm md:text-base uppercase tracking-widest mb-4">
              Suelos indestructibles para gimnasios
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black uppercase tracking-tight leading-[1.1] mb-8" data-testid="hero-title">
              Suelos de caucho<br />
              para gimnasios,<br />
              <span className="text-[#9ca3af]">¡diseñados para durar!</span>
            </h1>
            <p className="text-lg md:text-xl text-[var(--degoma-text)] max-w-xl mb-8">
              Losetas 100×100 cm en 5 espesores para Fitness, CrossFit, boxeo y zonas de fuerza. Instalación rápida sin adhesivos.
            </p>

            {/* Feature bullets */}
            <div className="flex flex-wrap gap-4 md:gap-6 mb-8">
              <div className="flex items-center gap-2 text-sm md:text-base">
                <Shield size={20} className="text-[var(--degoma-red)]" />
                <span>Absorción de impacto</span>
              </div>
              <div className="flex items-center gap-2 text-sm md:text-base">
                <Grip size={20} className="text-[var(--degoma-red)]" />
                <span>Superficie con agarre</span>
              </div>
              <div className="flex items-center gap-2 text-sm md:text-base">
                <Layers size={20} className="text-[var(--degoma-red)]" />
                <span>Rápida instalación</span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button
                onClick={() => scrollToSection('contacto')}
                className="btn-primary px-8 py-4 text-lg animate-pulse-glow"
                data-testid="hero-cta-primary">

                Pedir presupuesto <ArrowRight size={20} />
              </Button>
              <Button onClick={openWhatsApp} className="btn-whatsapp px-8 py-4 text-lg" data-testid="hero-cta-whatsapp">
                <MessageCircle size={20} /> Hablar por WhatsApp
              </Button>
            </div>

            <button
              onClick={() => scrollToSection('espesores')}
              className="text-[var(--degoma-text)] hover:text-white transition-colors flex items-center gap-2 text-sm"
              data-testid="hero-cta-tertiary">

              Ver espesores <ChevronDown size={16} />
            </button>

            {/* Trust strip */}
            <div className="trust-strip">
              <div className="trust-badge">
                <Factory size={16} className="text-[var(--degoma-red)]" />
                <span>Fabricantes desde 1978</span>
              </div>
              <div className="trust-badge">
                <Truck size={16} className="text-[var(--degoma-red)]" />
                <span>Entrega rápida*</span>
              </div>
              <div className="trust-badge">
                <FileText size={16} className="text-[var(--degoma-red)]" />
                <span>Asesoramiento gratuito</span>
              </div>
            </div>
            <p className="text-xs text-[var(--degoma-muted)] mt-3">*Material en stock. Plazo según destino y disponibilidad.</p>
          </div>

          {/* Hero visual - Texture preview */}
          <div className="hidden lg:block">
            <div className="relative">
              <div className="aspect-square max-w-md mx-auto rounded-lg overflow-hidden border border-[var(--degoma-border)] card-hover">
                <img
                  src={ASSETS.texture}
                  alt="Textura grano ultrafino"
                  className="w-full h-full object-cover" />

              </div>
              <div className="absolute -bottom-4 -right-4 bg-[var(--degoma-surface)] border border-[var(--degoma-border)] rounded-lg p-4">
                <p className="text-xs text-[var(--degoma-muted)] uppercase tracking-wider mb-1">Acabado</p>
                <p className="font-semibold">Grano ultrafino</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="galeria" className="section section-darker" data-testid="gallery-section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold mb-4" data-testid="gallery-title">
              ¡Lleva tu entrenamiento al siguiente nivel!
            </h2>
            <p className="text-[var(--degoma-text)]">Suelos de calidad profesional, que promueven la actividad deportiva...
            </p>
          </div>

          {/* Grid galería - diseño estético */}
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              
              {/* Fila 1: Imagen grande + 2 pequeñas */}
              <div className="col-span-2 row-span-2 aspect-square overflow-hidden rounded-xl img-zoom bg-[var(--degoma-surface)]">
                <img src={ASSETS.crossfitGuy} alt="Entrenamiento en box CrossFit DEGOMA" className="w-full h-full object-cover" loading="eager" />
              </div>
              
              <div className="aspect-square overflow-hidden rounded-xl img-zoom bg-[var(--degoma-surface)]">
                <img src={ASSETS.gymNeon} alt="Gimnasio moderno con suelo DEGOMA" className="w-full h-full object-cover" loading="eager" />
              </div>
              
              <div className="aspect-square overflow-hidden rounded-xl img-zoom bg-[var(--degoma-surface)]">
                <img src={ASSETS.gymBenches} alt="Zona de pesas con suelo DEGOMA" className="w-full h-full object-cover" loading="eager" />
              </div>
              
              <div className="aspect-square overflow-hidden rounded-xl img-zoom bg-[var(--degoma-surface)]">
                <img src={ASSETS.athleteTop} alt="Atleta entrenando en suelo DEGOMA" className="w-full h-full object-cover" loading="eager" />
              </div>
              
              <div className="aspect-square overflow-hidden rounded-xl img-zoom bg-[var(--degoma-surface)]">
                <img src={ASSETS.gymFunctional} alt="Sala funcional con suelo DEGOMA" className="w-full h-full object-cover" loading="eager" />
              </div>

              {/* Fila 2: 4 imágenes pequeñas */}
              <div className="aspect-square overflow-hidden rounded-xl img-zoom bg-[var(--degoma-surface)]">
                <img src={ASSETS.texture} alt="Textura grano ultrafino" className="w-full h-full object-cover" loading="eager" />
              </div>
              
              <div className="aspect-square overflow-hidden rounded-xl img-zoom bg-[var(--degoma-surface)]">
                <img src={ASSETS.shipping} alt="Envío y logística DEGOMA" className="w-full h-full object-cover" loading="eager" />
              </div>
              
              <div className="col-span-2 aspect-[2/1] overflow-hidden rounded-xl img-zoom bg-[var(--degoma-surface)]">
                <img src={ASSETS.warehouse} alt="Almacén y logística DEGOMA" className="w-full h-full object-cover" loading="eager" />
              </div>
              
            </div>
          </div>
        </div>
      </section>

      {/* Thickness Selector Section */}
      <section id="espesores" className="section section-dark pattern-bg !pb-10 md:!pb-16" data-testid="thickness-section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold mb-4" data-testid="thickness-title">
              Elige el espesor ideal<br />para tu zona de entrenamiento
            </h2>
            <p className="text-[var(--degoma-text)] max-w-2xl mx-auto">
              Guía orientativa según tipo de actividad. Te confirmamos el espesor óptimo según tu equipamiento y uso específico.
            </p>
          </div>

          <div className="thickness-grid mb-8">
            {THICKNESS_OPTIONS.map((option) =>
            <button
              key={option.value}
              onClick={() => setSelectedThickness(option.value)}
              className={`thickness-card relative p-6 rounded-lg bg-[var(--degoma-surface)] border text-left hover-lift ${
              selectedThickness === option.value ? 'active' : ''}`
              }
              data-testid={`thickness-${option.value}`}>

                {option.popular && <span className="badge-popular">Más elegido</span>}
                {option.badge && <span className="badge-popular">{option.badge}</span>}
                
                <div className="thickness-indicator w-full h-2 bg-[var(--degoma-gray)] rounded mb-4" style={{
                height: `${parseInt(option.value) * 8}px`,
                maxHeight: '32px'
              }} />
                
                <h3 className="text-2xl font-bold mb-1">{option.label}</h3>
                <p className="text-[var(--degoma-red)] font-semibold text-sm mb-2">{option.use}</p>
                <p className="text-[var(--degoma-text)] text-sm">{option.description}</p>
              </button>
            )}
          </div>

          <p className="text-center text-[var(--degoma-muted)] text-sm mb-16">
            * Guía orientativa. Te confirmamos el espesor según tu equipamiento y uso.
          </p>

          {/* Technical Specifications Table */}
          <div className="mt-12" data-testid="specs-table">
            <div className="text-center mb-8">
              <p className="text-lg md:text-xl font-bold text-[var(--degoma-red)] tracking-wide">
                SUELOS RESISTENTES | CALIDAD PROFESIONAL | ¡DISEÑADOS PARA DURAR!
              </p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[var(--degoma-red)]">
                    <th className="px-4 py-4 text-left text-sm font-bold uppercase tracking-wider text-white">Referencia</th>
                    <th className="px-4 py-4 text-center text-sm font-bold uppercase tracking-wider text-white">Peso kg</th>
                    <th className="px-4 py-4 text-center text-sm font-bold uppercase tracking-wider text-white">Densidad</th>
                    <th className="px-4 py-4 text-center text-sm font-bold uppercase tracking-wider text-white">Acabado</th>
                    <th className="px-4 py-4 text-center text-sm font-bold uppercase tracking-wider text-white">Granulometría</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-[var(--degoma-surface)] border-b border-[var(--degoma-border)] transition-all duration-300 hover:bg-[var(--degoma-gray)] hover:scale-[1.01] cursor-default">
                    <td className="px-4 py-4 text-white font-semibold">Degoma® Gimnasio Premium 10</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">9.5</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">1000 KG / M³</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">LISA</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">Grano Ultra fino</td>
                  </tr>
                  <tr className="bg-[var(--degoma-gray)] border-b border-[var(--degoma-border)] transition-all duration-300 hover:bg-[var(--degoma-surface)] hover:scale-[1.01] cursor-default">
                    <td className="px-4 py-4 text-white font-semibold">Degoma® Gimnasio Premium 15</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">14</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">933 KG / M³</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">LISA</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">Grano Ultra fino</td>
                  </tr>
                  <tr className="bg-[var(--degoma-surface)] border-b border-[var(--degoma-border)] transition-all duration-300 hover:bg-[var(--degoma-gray)] hover:scale-[1.01] cursor-default">
                    <td className="px-4 py-4 text-white font-semibold">Degoma® Gimnasio Premium 20</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">18</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">900 KG / M³</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">LISA</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">Grano Ultra fino</td>
                  </tr>
                  <tr className="bg-[var(--degoma-gray)] border-b border-[var(--degoma-border)] transition-all duration-300 hover:bg-[var(--degoma-surface)] hover:scale-[1.01] cursor-default">
                    <td className="px-4 py-4 text-white font-semibold">Degoma® Gimnasio Premium 30</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">26.7</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">890 KG / M³</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">LISA</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">Grano Ultra fino</td>
                  </tr>
                  <tr className="bg-[var(--degoma-surface)] border-b border-[var(--degoma-border)] transition-all duration-300 hover:bg-[var(--degoma-gray)] hover:scale-[1.01] cursor-default">
                    <td className="px-4 py-4 text-white font-semibold">Degoma® Gimnasio Premium 40</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">33</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">825 KG / M³</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">LISA</td>
                    <td className="px-4 py-4 text-center text-[var(--degoma-text)]">Grano Ultra fino</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="section section-darker !pt-10 md:!pt-16" data-testid="calculator-section">
        <div className="container">
          <div className="max-w-xl mx-auto">
            <div className="calculator-box">
              <div className="flex items-center gap-3 mb-6">
                <Calculator size={24} className="text-[var(--degoma-red)]" />
                <h3 className="text-2xl font-bold">Calcula tus losetas</h3>
              </div>

              <div className="mb-6">
                <Label className="text-[var(--degoma-text)] mb-2 block">Superficie (m²)</Label>
                <Input
                  type="number"
                  placeholder="Introduce los metros cuadrados"
                  value={calculatorValue}
                  onChange={(e) => setCalculatorValue(e.target.value)}
                  className="input-dark text-lg py-4"
                  data-testid="calculator-input" />

              </div>

              <div className="bg-[var(--degoma-gray)] rounded-lg p-6 text-center mb-6">
                <p className="text-[var(--degoma-muted)] text-sm uppercase tracking-wider mb-2">Losetas estimadas</p>
                <p className="calculator-result" data-testid="calculator-result">
                  {calculatorValue ? Math.ceil(parseFloat(calculatorValue) || 0) : '—'}
                </p>
                <p className="text-[var(--degoma-text)] text-sm mt-2">1 loseta = 1 m²</p>
              </div>

              <p className="text-sm text-white text-center mb-6">
                Recuerda que en la instalación siempre hay recortes / mermas.<br />
                Añade entre un 5% a 10% Extra, para ir sobre seguro.
              </p>

              <Button
                onClick={() => {
                  if (calculatorValue) {
                    setFormData((prev) => ({ ...prev, superficie: calculatorValue }));
                  }
                  scrollToSection('contacto');
                }}
                className="btn-primary w-full py-4"
                data-testid="calculator-cta">

                Pedir presupuesto con mi superficie
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="ventajas" className="section section-dark" data-testid="benefits-section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6" data-testid="benefits-title">
                Diseñado para<br />uso intensivo
              </h2>
              <p className="text-[var(--degoma-text)] text-lg mb-10">
                Losetas de caucho orientadas a resistir el día a día de un gimnasio profesional. Acabado de grano ultrafino para facilitar la limpieza.
              </p>

              <div className="benefits-grid">
                {BENEFITS.map((benefit, index) =>
                <div key={index} className="flex gap-4" data-testid={`benefit-${index}`}>
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[var(--degoma-surface)] border border-[var(--degoma-border)] flex items-center justify-center">
                      <benefit.icon size={24} className="text-[var(--degoma-red)]" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1 text-base normal-case">{benefit.title}</h4>
                      <p className="text-[var(--degoma-text)] text-sm">{benefit.description}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="relative rounded-lg overflow-hidden border border-[var(--degoma-border)]">
              <img
                src={ASSETS.factoryTruck}
                alt="Fábrica DEGOMA - Fabricantes desde 1978"
                className="w-full h-auto" />
              <div className="absolute inset-0 bg-black/40"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Section */}
      <section id="instalacion" className="section section-dark" data-testid="installation-section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold mb-4" data-testid="installation-title">
              Instalación en 3 pasos
            </h2>
            <p className="text-[var(--degoma-text)] max-w-2xl mx-auto">
              Sin obra. Sin esperas. Coloca tú mismo o con tu equipo.
            </p>
          </div>

          <div className="steps-container mb-16">
            <div className="step-card" data-testid="step-1">
              <div className="step-number">1</div>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--degoma-gray)] flex items-center justify-center">
                <Square size={32} className="text-[var(--degoma-red)]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Prepara la superficie</h3>
              <p className="text-[var(--degoma-text)] text-sm">
                Asegúrate de que el suelo esté limpio, seco y nivelado.
              </p>
            </div>

            <div className="step-card" data-testid="step-2">
              <div className="step-number">2</div>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--degoma-gray)] flex items-center justify-center">
                <LayoutGrid size={32} className="text-[var(--degoma-red)]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Coloca y ajusta</h3>
              <p className="text-[var(--degoma-text)] text-sm">
                Encaja las losetas sin adhesivos. Corta los bordes si es necesario.
              </p>
            </div>

            <div className="step-card" data-testid="step-3">
              <div className="step-number">3</div>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--degoma-gray)] flex items-center justify-center">
                <Dumbbell size={32} className="text-[var(--degoma-red)]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Listo para entrenar</h3>
              <p className="text-[var(--degoma-text)] text-sm">
                Tu gimnasio ya tiene suelo profesional. Empieza a entrenar.
              </p>
            </div>
          </div>

          {/* Installation methods */}
          <div className="max-w-3xl mx-auto">
            <h3 className="text-xl font-bold text-center mb-6">3 formas de instalación</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="method-card" data-testid="method-flotante">
                <div className="method-icon">
                  <Layers size={24} />
                </div>
                <h4 className="font-semibold mb-1">Flotante</h4>
                <p className="text-[var(--degoma-text)] text-sm">Sin adhesivos. Fácil reubicación.</p>
              </div>
              <div className="method-card" data-testid="method-cinta">
                <div className="method-icon">
                  <Grip size={24} />
                </div>
                <h4 className="font-semibold mb-1">Cinta doble cara</h4>
                <p className="text-[var(--degoma-text)] text-sm">Fijación media. Desmontable.</p>
              </div>
              <div className="method-card" data-testid="method-pegado">
                <div className="method-icon">
                  <Shield size={24} />
                </div>
                <h4 className="font-semibold mb-1">Pegado</h4>
                <p className="text-[var(--degoma-text)] text-sm">Fijación permanente. Máxima estabilidad.</p>
              </div>
            </div>
            <p className="text-center text-[var(--degoma-muted)] text-sm mt-6">
              Te aconsejamos la opción según uso y superficie.
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="usos" className="section section-darker" data-testid="use-cases-section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold mb-4" data-testid="use-cases-title">
              Soluciones por tipo de negocio
            </h2>
            <p className="text-[var(--degoma-text)]">
              Cada zona tiene sus necesidades. Encuentra tu configuración.
            </p>
          </div>

          <div className="use-cases-grid">
            {USE_CASES.map((useCase, index) =>
            <div
              key={index}
              className="bg-[var(--degoma-surface)] rounded-lg border border-[var(--degoma-border)] p-6 card-hover"
              data-testid={`use-case-${index}`}>

                <div className="w-14 h-14 rounded-lg bg-[var(--degoma-gray)] flex items-center justify-center mb-4">
                  <useCase.icon size={28} className="text-[var(--degoma-red)]" />
                </div>
                <h3 className="text-xl font-bold mb-3">{useCase.title}</h3>
                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-2">
                    <span className="text-[var(--degoma-muted)] text-sm">Necesidad:</span>
                    <span className="text-[var(--degoma-text)] text-sm">{useCase.pain}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-[var(--degoma-muted)] text-sm">Beneficio:</span>
                    <span className="text-[var(--degoma-text)] text-sm">{useCase.benefit}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-[var(--degoma-border)]">
                  <div>
                    <p className="text-xs text-[var(--degoma-muted)] uppercase">Espesor orientativo</p>
                    <p className="font-bold text-[var(--degoma-red)]">{useCase.thickness}</p>
                  </div>
                  <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => scrollToSection('contacto')}
                  className="text-[var(--degoma-red)] hover:text-white hover:bg-[var(--degoma-red)]">

                    Quiero este <ArrowRight size={14} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-12 md:py-16 proof-section" data-testid="proof-section">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
            <p className="text-5xl md:text-6xl font-black text-[var(--degoma-red)]" data-testid="proof-number">+1000</p>
            <div className="text-center md:text-left">
              <p className="text-xl md:text-2xl font-bold uppercase tracking-wide">
                Gimnasios confían en nosotros
              </p>
              <p className="text-[var(--degoma-text)]">
                Península, Canarias y Baleares
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section section-dark" data-testid="faq-section">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-bold mb-4" data-testid="faq-title">
                Preguntas frecuentes
              </h2>
              <p className="text-[var(--degoma-text)]">
                Resolvemos tus dudas antes de que las tengas.
              </p>
            </div>

            <div className="space-y-0">
              {FAQ_DATA.map((faq, index) =>
              <div key={index} className="faq-item" data-testid={`faq-${index}`}>
                  <button
                  className="faq-trigger"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  data-testid={`faq-trigger-${index}`}>

                    <h3>{faq.question}</h3>
                    {openFaq === index ?
                  <ChevronUp size={20} className="text-[var(--degoma-red)] flex-shrink-0" /> :

                  <ChevronDown size={20} className="text-[var(--degoma-muted)] flex-shrink-0" />
                  }
                  </button>
                  {openFaq === index &&
                <div className="faq-answer animate-fade-in" data-testid={`faq-answer-${index}`}>
                      {faq.answer}
                    </div>
                }
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contacto" className="section form-section" data-testid="contact-section">
        <div className="container relative z-10">
          <div className="form-container">
            <div>
              <h2 className="text-3xl md:text-5xl font-bold mb-4" data-testid="contact-title">
                Pide tu presupuesto<br />en 1 minuto
              </h2>
              <p className="text-[var(--degoma-text)] text-lg mb-8">
                Completa el formulario y te enviamos una propuesta personalizada. Sin compromiso.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <Check size={20} className="text-[var(--degoma-red)]" />
                  <span className="text-[var(--degoma-text)]">Respuesta en menos de 24h laborables</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check size={20} className="text-[var(--degoma-red)]" />
                  <span className="text-[var(--degoma-text)]">Presupuesto detallado sin compromiso</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check size={20} className="text-[var(--degoma-red)]" />
                  <span className="text-[var(--degoma-text)]">Asesoramiento sobre espesor y cantidad</span>
                </div>
              </div>

              <div className="bg-[var(--degoma-gray)] rounded-lg p-6 border border-[var(--degoma-border)]">
                <p className="font-semibold mb-4">¿Prefieres hablar directamente?</p>
                <div className="space-y-3">
                  <button onClick={openWhatsApp} className="contact-item w-full text-left">
                    <MessageCircle size={20} />
                    <span>WhatsApp: 618 265 667</span>
                  </button>
                  <a href="tel:618265712" className="contact-item">
                    <Phone size={20} />
                    <span>Teléfono: 618 265 712</span>
                  </a>
                  <a href="mailto:hola@degoma.es" className="contact-item">
                    <Mail size={20} />
                    <span>hola@degoma.es</span>
                  </a>
                </div>
              </div>
            </div>

            <div>
              <form onSubmit={handleSubmit} className="bg-[var(--degoma-surface)] rounded-lg p-6 md:p-8 border border-[var(--degoma-border)]" data-testid="quote-form">
                <div className="form-grid mb-6">
                  <div className="col-span-full sm:col-span-1">
                    <Label className="text-white mb-2 block">Nombre *</Label>
                    <Input
                      type="text"
                      placeholder="Tu nombre"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="input-dark"
                      required
                      data-testid="form-nombre" />

                  </div>
                  <div className="col-span-full sm:col-span-1">
                    <Label className="text-white mb-2 block">Email *</Label>
                    <Input
                      type="email"
                      placeholder="tu@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-dark"
                      required
                      data-testid="form-email" />

                  </div>
                  <div className="col-span-full sm:col-span-1">
                    <Label className="text-white mb-2 block">Teléfono *</Label>
                    <Input
                      type="tel"
                      placeholder="Ej. 600 123 456"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      className="input-dark"
                      required
                      data-testid="form-telefono" />

                  </div>
                  <div className="col-span-full sm:col-span-1">
                    <Label className="text-white mb-2 block">Ciudad *</Label>
                    <Input
                      type="text"
                      placeholder="Ubicación del gimnasio"
                      value={formData.ciudad}
                      onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                      className="input-dark"
                      required
                      data-testid="form-ciudad" />

                  </div>
                  <div className="col-span-full sm:col-span-1">
                    <Label className="text-white mb-2 block">Superficie (m²) *</Label>
                    <Input
                      type="number"
                      placeholder="Metros cuadrados"
                      value={formData.superficie}
                      onChange={(e) => setFormData({ ...formData, superficie: e.target.value })}
                      className="input-dark"
                      required
                      data-testid="form-superficie" />

                  </div>
                  <div className="col-span-full">
                    <Label className="text-white mb-2 block">Tipo de gimnasio *</Label>
                    <Select
                      value={formData.tipo_gimnasio}
                      onValueChange={(value) => setFormData({ ...formData, tipo_gimnasio: value })}>

                      <SelectTrigger className="input-dark" data-testid="form-tipo-gimnasio">
                        <SelectValue placeholder="Selecciona tipo de gimnasio" />
                      </SelectTrigger>
                      <SelectContent
                        className="bg-[var(--degoma-surface)] border-[var(--degoma-border)] z-[100]"
                        position="popper"
                        sideOffset={4}>

                        {GYM_TYPES.map((type) =>
                        <SelectItem
                          key={type}
                          value={type}
                          className="text-white hover:bg-[var(--degoma-gray)] cursor-pointer focus:bg-[var(--degoma-gray)] focus:text-white">

                            {type}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mb-6">
                  <Label className="text-white mb-3 block">Espesor de interés * (puedes seleccionar varios)</Label>
                  <div className="checkbox-grid">
                    {THICKNESS_OPTIONS.map((option) =>
                    <label
                      key={option.value}
                      className={`checkbox-item ${formData.espesores.includes(option.value) ? 'checked' : ''}`}
                      data-testid={`form-espesor-${option.value}`}>

                        <input
                        type="checkbox"
                        checked={formData.espesores.includes(option.value)}
                        onChange={() => handleThicknessToggle(option.value)}
                        className="w-4 h-4" />

                        <span className="text-sm font-medium">{option.label}</span>
                      </label>
                    )}
                  </div>
                </div>

                <div className="mb-6">
                  <Label className="text-white mb-2 block">Mensaje (opcional)</Label>
                  <Textarea
                    placeholder="Cuéntanos más sobre tu proyecto..."
                    value={formData.mensaje}
                    onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
                    className="input-dark min-h-[100px]"
                    data-testid="form-mensaje" />

                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full py-4 text-lg"
                  data-testid="form-submit">

                  {isSubmitting ? "Enviando..." : "Enviar y recibir propuesta"}
                </Button>

                <p className="text-xs text-[var(--degoma-muted)] text-center mt-4">
                  Tus datos están protegidos. Sin spam, sin compromiso.
                </p>
              </form>

              <div className="mt-6 text-center">
                <p className="text-[var(--degoma-text)] mb-3">¿Hablamos?</p>
                <Button onClick={openWhatsApp} className="btn-whatsapp px-8 py-4" data-testid="contact-whatsapp">
                  <MessageCircle size={20} /> WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="container">
          <div className="footer-grid mb-12">
            <div>
              <a href="https://degoma.es/" target="_blank" rel="noopener noreferrer" className="logo-text inline-block mb-4">DEGOMA<sup style={{ fontSize: '12px' }}>®</sup></a>
              
              {/* Badge Fabricantes desde 1978 */}
              <div className="flex items-center gap-3 mb-4 p-3 bg-[var(--degoma-surface)] border border-[var(--degoma-border)] rounded-lg">
                <div className="w-10 h-10 rounded-full bg-[var(--degoma-gray)] flex items-center justify-center flex-shrink-0">
                  <Factory size={20} className="text-[var(--degoma-red)]" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Fabricantes desde 1978</p>
                  <p className="text-[var(--degoma-text)] text-xs">Más de 45 años de experiencia</p>
                </div>
              </div>
              
              <p className="text-[var(--degoma-text)] mb-4 max-w-sm">Losetas 100×100 Especial Gimnasios
                <br />DEGOMA® GYM | 5 espesores disponibles

              </p>
              <p className="text-[var(--degoma-text)] text-sm">Envíos a toda España: Península, Canarias y Baleares.
También a Andorra, Ceuta y Melilla.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4 normal-case">Contacto</h4>
              <div className="space-y-3">
                <a href="mailto:hola@degoma.es" className="contact-item">
                  <Mail size={18} />
                  <span>hola@degoma.es</span>
                </a>
                <a href="tel:618265712" className="contact-item">
                  <Phone size={18} />
                  <span>618 265 712</span>
                </a>
                <a href="tel:618265667" className="contact-item">
                  <Phone size={18} />
                  <span>618 265 667</span>
                </a>
                <button onClick={openWhatsApp} className="contact-item w-full text-left">
                  <MessageCircle size={18} />
                  <span>WhatsApp</span>
                </button>
                <a href="https://degoma.es/" target="_blank" rel="noopener noreferrer" className="contact-item">
                  <Link2 size={18} />
                  <span>degoma.es</span>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4 normal-case">Navegación</h4>
              <nav className="space-y-2">
                <button onClick={() => scrollToSection('espesores')} className="block text-[var(--degoma-text)] hover:text-white transition-colors text-sm">Espesores</button>
                <button onClick={() => scrollToSection('ventajas')} className="block text-[var(--degoma-text)] hover:text-white transition-colors text-sm">Ventajas</button>
                <button onClick={() => scrollToSection('instalacion')} className="block text-[var(--degoma-text)] hover:text-white transition-colors text-sm">Instalación</button>
                <button onClick={() => scrollToSection('usos')} className="block text-[var(--degoma-text)] hover:text-white transition-colors text-sm">Casos de uso</button>
                <button onClick={() => scrollToSection('faq')} className="block text-[var(--degoma-text)] hover:text-white transition-colors text-sm">FAQ</button>
              </nav>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4 normal-case">Síguenos</h4>
              <div className="flex gap-3 mb-6">
                <a
                  href="https://www.instagram.com/degoma.es/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  data-testid="social-instagram">

                  <Instagram size={20} />
                </a>
                <a
                  href="https://www.facebook.com/Degoma.es"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  data-testid="social-facebook">

                  <Facebook size={20} />
                </a>
                <a
                  href="https://degoma.es/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  data-testid="social-web">

                  <Globe size={20} />
                </a>
              </div>
              <Button
                onClick={() => scrollToSection('contacto')}
                className="btn-primary w-full"
                data-testid="footer-cta">

                Pedir presupuesto
              </Button>
            </div>
          </div>

          <div className="pt-8 border-t border-[var(--degoma-border)] text-center">
            <p className="text-[var(--degoma-muted)] text-sm">
              © {new Date().getFullYear()} DEGOMA. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Mobile Sticky Bar */}
      <div className="mobile-sticky-bar md:hidden" data-testid="mobile-sticky-bar">
        <button
          onClick={openWhatsApp}
          className="bg-[#25D366] text-white">

          <MessageCircle size={20} />
          <span>WhatsApp</span>
        </button>
        <button
          onClick={() => scrollToSection('contacto')}
          className="bg-[var(--degoma-red)] text-white">

          <ArrowRight size={20} />
          <span>Presupuesto</span>
        </button>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop &&
      <button
        onClick={scrollToTop}
        className="fixed bottom-24 md:bottom-8 right-4 md:right-8 z-50 w-12 h-12 bg-[var(--degoma-red)] hover:bg-[var(--degoma-red-hover)] text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
        data-testid="scroll-to-top"
        aria-label="Volver arriba">

          <ChevronUp size={24} />
        </button>
      }
    </div>);

}

export default App;